import {
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { LoadableState, SavedSearch } from '~/contracts/common';
import type { DeepPartial } from '~/types';

export type SavedSearchesContextType = {
  fetchSavedSearchTrigger: number;
  savedSearches: LoadableState<SavedSearch[]>;
  triggerFetchSavedSearches: () => void;
};

export const defaultContext: SavedSearchesContextType = {
  fetchSavedSearchTrigger: 0,
  savedSearches: defaultLoadableState<SavedSearch[]>([]),
  triggerFetchSavedSearches() {}
};

const SavedSearchesContext =
  createContext<SavedSearchesContextType>(defaultContext);

export function SavedSearchesContextProvider({
  children
}: React.PropsWithChildren) {
  const [fetchSavedSearchTrigger, setTrigger] = useState<number>(
    defaultContext.fetchSavedSearchTrigger
  );
  const savedSearches = useLoadableState<SavedSearch[]>([]);

  const triggerFetchSavedSearches = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  const value = useMemo<SavedSearchesContextType>(
    () => ({
      fetchSavedSearchTrigger,
      savedSearches,
      triggerFetchSavedSearches
    }),
    [fetchSavedSearchTrigger, savedSearches, triggerFetchSavedSearches]
  );

  return (
    <SavedSearchesContext.Provider value={value}>
      {children}
    </SavedSearchesContext.Provider>
  );
}

SavedSearchesContextProvider.displayName = 'SavedSearchesContextProvider';

export const generateSavedSearchesContext = (
  customState: DeepPartial<SavedSearchesContextType>
): SavedSearchesContextType =>
  deepmerge<SavedSearchesContextType, DeepPartial<SavedSearchesContextType>>(
    defaultContext,
    customState
  );

export function MockedSavedSearchesContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{
  mockValue: DeepPartial<SavedSearchesContextType>;
}>) {
  return (
    <SavedSearchesContext.Provider
      value={generateSavedSearchesContext(mockValue)}
    >
      {children}
    </SavedSearchesContext.Provider>
  );
}

export default SavedSearchesContext;

export function useSavedSearches() {
  const { savedSearches } = useContext(SavedSearchesContext);

  return savedSearches.data;
}

export function useSavedSearchesIsLoaded(): boolean {
  const { savedSearches } = useContext(SavedSearchesContext);

  return savedSearches.isLoaded;
}
